import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Save, Trash2 } from "lucide-react";
import { LoadingState } from "components";
import { usePaymentBanking } from "hooks";
import { PaymentBanking, PaymentBankingForm, PaymentBankingItem } from "types";
import { ROUTES } from "utils";
import SnackbarUtils from "utils/SnackbarUtils";

const todayString = () => new Date().toISOString().slice(0, 10);
const ATTACHMENT_SPLIT_REGEX = /[,\n]/;

const defaultForm: PaymentBankingForm = {
  payment_code: "",
  payment_date: todayString(),
  bank_account_id: undefined,
  bank_id: undefined,
  trader_name: "",
  trader_address: "",
  receiver_name: "",
  receiver_address: "",
  receiver_account: "",
  receiver_bank: "",
  import_invoice_id: undefined,
  description: "",
  attachments: [],
  items: [],
};

const defaultItem: PaymentBankingItem = {
  amount: 0,
  partner_id: undefined,
  expense_reason_id: undefined,
  description: "",
};

const parseNumber = (value: unknown): number => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const parseId = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  const parsed = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeAttachments = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "string" ? item : String(item ?? "")))
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => (typeof item === "string" ? item : String(item ?? "")))
          .map((item) => item.trim())
          .filter(Boolean);
      }
    } catch {
      // ignore invalid JSON strings
    }

    return trimmed
      .split(ATTACHMENT_SPLIT_REGEX)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const sanitizeItems = (items?: PaymentBankingItem[]): PaymentBankingItem[] =>
  Array.isArray(items)
    ? items
        .map((item) => ({
          amount: parseNumber(item.amount),
          partner_id: parseId(item.partner_id),
          expense_reason_id: parseId(item.expense_reason_id),
          description: item.description
            ? String(item.description).trim()
            : undefined,
        }))
        .filter(
          (item) =>
            item.amount ||
            item.partner_id ||
            item.expense_reason_id ||
            Boolean(item.description),
        )
    : [];

const PaymentBankingDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const { fetchDetail, createPaymentBanking, updatePaymentBanking } =
    usePaymentBanking();

  const { data: detailResponse, isFetching: isFetchingDetail } = fetchDetail(
    id ?? "",
    isEdit,
  );

  const [form, setForm] = useState<PaymentBankingForm>(defaultForm);
  const [items, setItems] = useState<PaymentBankingItem[]>([defaultItem]);
  const [attachmentsText, setAttachmentsText] = useState<string>("");

  useEffect(() => {
    if (!detailResponse?.data) return;
    const detail = detailResponse.data as PaymentBanking;
    const normalizedAttachments = normalizeAttachments(detail.attachments);
    const normalizedItems = sanitizeItems(detail.items);
    setForm({
      payment_code: detail.payment_code || "",
      payment_date: detail.payment_date || todayString(),
      bank_account_id: parseId(detail.bank_account_id),
      bank_id: parseId(detail.bank_id),
      trader_name: detail.trader_name || "",
      trader_address: detail.trader_address || "",
      receiver_name: detail.receiver_name || "",
      receiver_address: detail.receiver_address || "",
      receiver_account: detail.receiver_account || "",
      receiver_bank: detail.receiver_bank || "",
      import_invoice_id: parseId(detail.import_invoice_id),
      description: detail.description || "",
      attachments: normalizedAttachments,
      items: normalizedItems,
    });
    setAttachmentsText(normalizedAttachments.join(", "));
    setItems(normalizedItems.length ? normalizedItems : [defaultItem]);
  }, [detailResponse]);

  useEffect(() => {
    if (isEdit) return;
    setForm(defaultForm);
    setItems([defaultItem]);
    setAttachmentsText("");
  }, [isEdit]);

  const totalAmount = useMemo(
    () => items.reduce((sum, item) => sum + (Number(item.amount ?? 0) || 0), 0),
    [items],
  );

  const isSaving =
    createPaymentBanking.isPending || updatePaymentBanking.isPending;

  const handleFormChange = useCallback(
    (key: keyof PaymentBankingForm, value: unknown) => {
      setForm((prev) => ({
        ...prev,
        [key]:
          key === "bank_account_id" ||
          key === "bank_id" ||
          key === "import_invoice_id"
            ? parseId(value)
            : typeof value === "string"
              ? value
              : (value ?? ""),
      }));
    },
    [],
  );

  const handleAddItem = useCallback(() => {
    setItems((prev) => [...prev, { ...defaultItem }]);
  }, []);

  const handleRemoveItem = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleItemChange = useCallback(
    (
      index: number,
      key: keyof Pick<
        PaymentBankingItem,
        "amount" | "partner_id" | "expense_reason_id" | "description"
      >,
      value: unknown,
    ) => {
      setItems((prev) =>
        prev.map((item, i) =>
          i === index
            ? {
                ...item,
                [key]:
                  key === "amount"
                    ? parseNumber(value)
                    : key === "description"
                      ? String(value ?? "").trim()
                      : parseId(value),
              }
            : item,
        ),
      );
    },
    [],
  );

  const handleSubmit = useCallback(() => {
    const normalizedAttachments = normalizeAttachments(
      attachmentsText || form.attachments,
    );
    const normalizedItems = sanitizeItems(items);

    if (!form.payment_date || !form.trader_name || !form.receiver_name) {
      SnackbarUtils.error("Ngày chi, Người nộp và Người nhận là bắt buộc");
      return;
    }

    if (!normalizedItems.length || !normalizedItems[0].amount) {
      SnackbarUtils.error(
        "Vui lòng nhập ít nhất một dòng chi tiết với số tiền",
      );
      return;
    }

    const payload: PaymentBankingForm = {
      ...form,
      bank_account_id: parseId(form.bank_account_id),
      bank_id: parseId(form.bank_id),
      import_invoice_id: parseId(form.import_invoice_id),
      attachments: normalizedAttachments,
      items: normalizedItems,
    };

    if (isEdit && id) {
      updatePaymentBanking.mutate(
        { id, ...payload },
        {
          onSuccess: () => {
            navigate(ROUTES.ACCOUNTING.BANK_PAYMENT);
          },
          onError: () => {
            SnackbarUtils.error(
              "Cập nhật phiếu chi ngân hàng không thành công",
            );
          },
        },
      );
      return;
    }

    createPaymentBanking.mutate(payload, {
      onSuccess: () => {
        navigate(ROUTES.ACCOUNTING.BANK_PAYMENT);
      },
      onError: () => {
        SnackbarUtils.error("Tạo phiếu chi ngân hàng không thành công");
      },
    });
  }, [
    attachmentsText,
    createPaymentBanking,
    form,
    id,
    isEdit,
    items,
    navigate,
    updatePaymentBanking,
  ]);

  const handleBack = useCallback(() => {
    navigate(ROUTES.ACCOUNTING.BANK_PAYMENT);
  }, [navigate]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-gray-900">
            {isEdit
              ? "Cập nhật phiếu chi ngân hàng"
              : "Thêm phiếu chi ngân hàng"}
          </h1>
          <p className="text-sm text-gray-600">
            Nhập thông tin phiếu chi ngân hàng theo mẫu.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4" />
            Quay lại danh sách
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-70"
            disabled={isSaving}
          >
            <Save className="h-4 w-4" />
            {isSaving ? "Đang lưu..." : "Lưu phiếu chi"}
          </button>
        </div>
      </div>

      <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Mã phiếu
            </label>
            <input
              value={form.payment_code ?? ""}
              onChange={(e) => handleFormChange("payment_code", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="CG00001"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Ngày chi
            </label>
            <input
              type="date"
              value={form.payment_date ?? ""}
              onChange={(e) => handleFormChange("payment_date", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Tài khoản NH
            </label>
            <input
              type="number"
              value={form.bank_account_id ?? ""}
              onChange={(e) =>
                handleFormChange("bank_account_id", e.target.value)
              }
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="1"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Ngân hàng
            </label>
            <input
              type="number"
              value={form.bank_id ?? ""}
              onChange={(e) => handleFormChange("bank_id", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="1"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Người nộp
            </label>
            <input
              value={form.trader_name ?? ""}
              onChange={(e) => handleFormChange("trader_name", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="Nguyễn Văn A"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Địa chỉ người nộp
            </label>
            <input
              value={form.trader_address ?? ""}
              onChange={(e) =>
                handleFormChange("trader_address", e.target.value)
              }
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="Hà Nội"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Người nhận
            </label>
            <input
              value={form.receiver_name ?? ""}
              onChange={(e) =>
                handleFormChange("receiver_name", e.target.value)
              }
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="Công ty ABC"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Địa chỉ nhận
            </label>
            <input
              value={form.receiver_address ?? ""}
              onChange={(e) =>
                handleFormChange("receiver_address", e.target.value)
              }
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="TP.HCM"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Tài khoản nhận
            </label>
            <input
              value={form.receiver_account ?? ""}
              onChange={(e) =>
                handleFormChange("receiver_account", e.target.value)
              }
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="0123456789"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Ngân hàng nhận
            </label>
            <input
              value={form.receiver_bank ?? ""}
              onChange={(e) =>
                handleFormChange("receiver_bank", e.target.value)
              }
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="Vietcombank"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Hóa đơn nhập
            </label>
            <input
              type="number"
              value={form.import_invoice_id ?? ""}
              onChange={(e) =>
                handleFormChange("import_invoice_id", e.target.value)
              }
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="10"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Mô tả</label>
            <textarea
              value={form.description ?? ""}
              onChange={(e) => handleFormChange("description", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              rows={3}
              placeholder="Chi thanh toán tiền hàng"
            />
          </div>
          <div className="space-y-1 md:col-span-2">
            <label className="text-sm font-medium text-gray-700">
              Tệp đính kèm
            </label>
            <textarea
              value={attachmentsText}
              onChange={(e) => setAttachmentsText(e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              rows={2}
              placeholder='Ngăn cách bằng dấu "," hoặc xuống dòng'
            />
          </div>
        </div>

        <div className="rounded-lg border border-gray-200">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-gray-900">
                Chi tiết chi
              </p>
              <p className="text-xs text-gray-500">
                Đối tác, lý do chi và số tiền.
              </p>
            </div>
            <button
              type="button"
              onClick={handleAddItem}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100"
            >
              <Plus className="h-4 w-4" />
              Thêm dòng
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">#</th>
                  <th className="px-4 py-2 text-left font-semibold">Đối tác</th>
                  <th className="px-4 py-2 text-left font-semibold">
                    Lý do chi
                  </th>
                  <th className="px-4 py-2 text-left font-semibold">Mô tả</th>
                  <th className="px-4 py-2 text-right font-semibold">
                    Số tiền
                  </th>
                  <th className="px-4 py-2 text-center font-semibold">
                    Hành động
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {items.length ? (
                  items.map((item, index) => (
                    <tr key={`item-${index}`} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-500">{index + 1}</td>
                      <td className="px-4 py-2 text-gray-900">
                        <input
                          type="number"
                          value={item.partner_id ?? ""}
                          onChange={(e) =>
                            handleItemChange(
                              index,
                              "partner_id",
                              e.target.value,
                            )
                          }
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                          placeholder="Mã đối tác"
                        />
                      </td>
                      <td className="px-4 py-2 text-gray-900">
                        <input
                          type="number"
                          value={item.expense_reason_id ?? ""}
                          onChange={(e) =>
                            handleItemChange(
                              index,
                              "expense_reason_id",
                              e.target.value,
                            )
                          }
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                          placeholder="Lý do chi"
                        />
                      </td>
                      <td className="px-4 py-2 text-gray-700">
                        <input
                          value={item.description ?? ""}
                          onChange={(e) =>
                            handleItemChange(
                              index,
                              "description",
                              e.target.value,
                            )
                          }
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                          placeholder="Mô tả"
                        />
                      </td>
                      <td className="px-4 py-2 text-right text-gray-900">
                        <input
                          type="number"
                          value={item.amount ?? 0}
                          onChange={(e) =>
                            handleItemChange(index, "amount", e.target.value)
                          }
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm text-right focus:border-blue-500 focus:outline-none"
                          placeholder="0"
                        />
                      </td>
                      <td className="px-4 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className="text-xs font-semibold text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="mr-1 inline h-4 w-4" />
                          Xóa
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      className="px-4 py-4 text-center text-gray-500"
                      colSpan={6}
                    >
                      Chưa có dòng chi tiết. Bấm "Thêm dòng" để bắt đầu.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3">
            <p className="text-sm text-gray-500">
              Tổng cộng:{" "}
              <span className="font-semibold text-gray-900">
                {totalAmount.toLocaleString("vi-VN")} đ
              </span>
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-70"
                disabled={isSaving}
              >
                <Save className="h-4 w-4" />
                {isSaving ? "Đang lưu..." : "Lưu phiếu chi NH"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {(isFetchingDetail || isSaving) && (
        <LoadingState
          message={
            isEdit
              ? "Đang tải phiếu chi ngân hàng"
              : "Đang lưu phiếu chi ngân hàng"
          }
          description="Vui lòng chờ trong giây lát."
        />
      )}
    </div>
  );
};

export default PaymentBankingDetail;
